<script lang="ts">
  // Parametric node-graph editor — Grasshopper-style visual programming.
  //
  // Interaction model:
  //   - Click empty canvas       → deselect
  //   - Drag node header         → move
  //   - Click output port → drag → input port = wire
  //   - Right-click / + button   → add-node menu (categorized)
  //   - Click node               → select (parameters editable in inspector)
  //   - Delete / Backspace       → remove selected
  //   - Wheel                    → pan-zoom
  //
  // Wires are SVG bezier paths drawn over an HTML canvas grid background.
  // Nodes are absolutely-positioned divs over the canvas.
  import { onMount, onDestroy } from 'svelte';
  import { app, persistSettings } from './store.svelte';
  import {
    NODE_REGISTRY,
    nodesByCategory
  } from '../core/paramNodes';
  import {
    evalGraph,
    newId,
    sitOnPlate,
    type ParamNode,
    type Wire,
    type ParamGraph
  } from '../core/paramGraph';
  import { applyPatternDisplacement } from '../core/cpuDisplace';
  import Icon from './lib/Icon.svelte';

  let containerEl: HTMLDivElement | undefined = $state();
  let cw = $state(800);
  let ch = $state(600);
  let view = $state({ ox: 200, oy: 100, scale: 1 });

  const NODE_W = 180;
  const ROW_H = 22;

  /** Compute pixel y of a port (input or output) within a node card. */
  function portY(node: ParamNode, portIndex: number): number {
    return node.y + 36 + portIndex * ROW_H + 8;
  }
  function portX(node: ParamNode, side: 'in' | 'out'): number {
    return side === 'in' ? node.x : node.x + NODE_W;
  }

  // ---- Drag/wire state ----
  type DragState =
    | { kind: 'node'; nodeId: string; offX: number; offY: number }
    | { kind: 'wire'; fromNode: string; fromPort: string; mouseX: number; mouseY: number }
    | { kind: 'pan'; startX: number; startY: number; ox0: number; oy0: number }
    | null;
  let drag: DragState = $state(null);

  let selectedNodeId = $state<string | null>(null);
  let menuOpen = $state(false);
  let menuPos = $state({ x: 0, y: 0 });

  // ---- Eval pipeline ----
  let evalErrors = $state<{ nodeId: string; message: string }[]>([]);
  let triCount = $state(0);

  function runEval() {
    const result = evalGraph(app.paramGraph, NODE_REGISTRY);
    evalErrors = result.errors;
    triCount = result.triangles.length;
    const seated = sitOnPlate(result.triangles);
    // Apply chat-set surface pattern as a post-process so it survives
    // every param tweak (slider drag wouldn't otherwise re-trigger the
    // displacement applied by the original tool call).
    app.triangles = app.paramPattern
      ? applyPatternDisplacement(seated, app.paramPattern)
      : seated;
  }

  // Re-eval whenever the graph changes (param edits, wires, nodes).
  $effect(() => {
    void app.paramGraph.nodes.length;
    void app.paramGraph.wires.length;
    // Deep-track param values + positions
    for (const n of app.paramGraph.nodes) {
      void n.x; void n.y; void n.type;
      for (const k of Object.keys(n.params)) void n.params[k];
    }
    runEval();
  });

  // ---- Coordinate helpers ----
  function screenToWorld(x: number, y: number): { x: number; y: number } {
    return { x: (x - view.ox) / view.scale, y: (y - view.oy) / view.scale };
  }

  // ---- Node ops ----
  function addNode(type: string, screenX: number, screenY: number) {
    const def = NODE_REGISTRY.get(type);
    if (!def) return;
    const w = screenToWorld(screenX, screenY);
    const params: Record<string, unknown> = {};
    for (const p of def.inputs) if (p.defaultValue !== undefined) params[p.id] = p.defaultValue;
    if (def.params) for (const p of def.params) if (p.defaultValue !== undefined) params[p.id] = p.defaultValue;
    const node: ParamNode = { id: newId('n'), type, x: w.x, y: w.y, params };
    app.paramGraph = { nodes: [...app.paramGraph.nodes, node], wires: app.paramGraph.wires };
    selectedNodeId = node.id;
    menuOpen = false;
  }

  function deleteSelected() {
    if (!selectedNodeId) return;
    const id = selectedNodeId;
    app.paramGraph = {
      nodes: app.paramGraph.nodes.filter((n) => n.id !== id),
      wires: app.paramGraph.wires.filter((w) => w.fromNode !== id && w.toNode !== id)
    };
    selectedNodeId = null;
  }

  function deleteWire(wireId: string) {
    app.paramGraph = {
      nodes: app.paramGraph.nodes,
      wires: app.paramGraph.wires.filter((w) => w.id !== wireId)
    };
  }

  function clearGraph() {
    if (!confirm('Tüm node\'ları sil?')) return;
    app.paramGraph = { nodes: [], wires: [] };
    selectedNodeId = null;
  }

  function addOutputIfMissing() {
    if (app.paramGraph.nodes.some((n) => n.type === 'output.mesh')) return;
    addNode('output.mesh', cw / 2 + 200, ch / 2);
  }

  // ---- Pointer handlers ----
  function onCanvasPointerDown(e: PointerEvent) {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      drag = { kind: 'pan', startX: e.clientX, startY: e.clientY, ox0: view.ox, oy0: view.oy };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();
      return;
    }
    if (e.button === 2) {
      const rect = containerEl!.getBoundingClientRect();
      menuPos = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      menuOpen = true;
      e.preventDefault();
      return;
    }
    selectedNodeId = null;
    menuOpen = false;
  }

  function onPointerMove(e: PointerEvent) {
    if (!drag) return;
    if (drag.kind === 'pan') {
      view = { ...view, ox: drag.ox0 + (e.clientX - drag.startX), oy: drag.oy0 + (e.clientY - drag.startY) };
      return;
    }
    if (drag.kind === 'node') {
      const w = screenToWorld(e.clientX - (containerEl?.getBoundingClientRect().left ?? 0),
                              e.clientY - (containerEl?.getBoundingClientRect().top ?? 0));
      const node = app.paramGraph.nodes.find((n) => n.id === drag!.nodeId);
      if (node) {
        node.x = w.x - drag.offX;
        node.y = w.y - drag.offY;
        // Trigger reactivity via reassignment
        app.paramGraph = { ...app.paramGraph };
      }
      return;
    }
    if (drag.kind === 'wire') {
      const rect = containerEl!.getBoundingClientRect();
      drag.mouseX = e.clientX - rect.left;
      drag.mouseY = e.clientY - rect.top;
      drag = { ...drag };
    }
  }

  function onPointerUp(e: PointerEvent) {
    if (drag?.kind === 'pan') {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    }
    drag = null;
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const rect = containerEl!.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const before = screenToWorld(px, py);
    const newScale = Math.max(0.3, Math.min(2.5, view.scale * factor));
    view = {
      scale: newScale,
      ox: px - before.x * newScale,
      oy: py - before.y * newScale
    };
  }

  function onKey(e: KeyboardEvent) {
    if (app.mode !== 'param') return;
    const t = e.target as HTMLElement;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT') return;
    if (e.key === 'Delete' || e.key === 'Backspace') { deleteSelected(); e.preventDefault(); }
  }

  // Node header drag
  function onNodeHeaderDown(e: PointerEvent, node: ParamNode) {
    e.stopPropagation();
    selectedNodeId = node.id;
    const rect = containerEl!.getBoundingClientRect();
    const w = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    drag = { kind: 'node', nodeId: node.id, offX: w.x - node.x, offY: w.y - node.y };
  }

  // Port drag-to-wire
  function onOutPortDown(e: PointerEvent, node: ParamNode, portId: string) {
    e.stopPropagation();
    const rect = containerEl!.getBoundingClientRect();
    drag = {
      kind: 'wire',
      fromNode: node.id,
      fromPort: portId,
      mouseX: e.clientX - rect.left,
      mouseY: e.clientY - rect.top
    };
  }
  function onInPortUp(e: PointerEvent, node: ParamNode, portId: string) {
    if (!drag || drag.kind !== 'wire') return;
    e.stopPropagation();
    if (drag.fromNode === node.id) { drag = null; return; }
    // Type-check: source port type must match target port type. Mismatch
    // silently rejects the connection — Grasshopper-style "no compatible".
    const fromN = app.paramGraph.nodes.find((n) => n.id === drag!.fromNode);
    const fromDef = fromN ? NODE_REGISTRY.get(fromN.type) : null;
    const toDef = NODE_REGISTRY.get(node.type);
    const fromPort = fromDef?.outputs.find((p) => p.id === drag!.fromPort);
    const toPort = toDef?.inputs.find((p) => p.id === portId);
    if (!fromPort || !toPort || fromPort.type !== toPort.type) {
      drag = null;
      return;
    }
    // Replace any existing wire into the same input port (single-input convention).
    const wires = app.paramGraph.wires.filter((w) => !(w.toNode === node.id && w.toPort === portId));
    wires.push({
      id: newId('w'),
      fromNode: drag.fromNode,
      fromPort: drag.fromPort,
      toNode: node.id,
      toPort: portId
    });
    app.paramGraph = { ...app.paramGraph, wires };
    drag = null;
  }

  function onParamInput(node: ParamNode, portId: string, value: string) {
    node.params[portId] = parseFloat(value) || 0;
    app.paramGraph = { ...app.paramGraph };
  }

  // Bezier wire path
  function wirePath(x1: number, y1: number, x2: number, y2: number): string {
    const dx = Math.max(40, Math.abs(x2 - x1) * 0.5);
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  }

  function getWireEndpoints(w: Wire): { x1: number; y1: number; x2: number; y2: number } | null {
    const fn = app.paramGraph.nodes.find((n) => n.id === w.fromNode);
    const tn = app.paramGraph.nodes.find((n) => n.id === w.toNode);
    if (!fn || !tn) return null;
    const fdef = NODE_REGISTRY.get(fn.type);
    const tdef = NODE_REGISTRY.get(tn.type);
    if (!fdef || !tdef) return null;
    const fIdx = fdef.outputs.findIndex((p) => p.id === w.fromPort);
    const tIdx = tdef.inputs.findIndex((p) => p.id === w.toPort);
    if (fIdx < 0 || tIdx < 0) return null;
    return {
      x1: portX(fn, 'out'),
      y1: portY(fn, fdef.inputs.length + fIdx), // outputs render below inputs
      x2: portX(tn, 'in'),
      y2: portY(tn, tIdx)
    };
  }

  // ---- Resize ----
  let resizeObs: ResizeObserver | null = null;
  function resize() {
    if (!containerEl) return;
    const r = containerEl.getBoundingClientRect();
    cw = Math.floor(r.width);
    ch = Math.floor(r.height);
  }

  onMount(() => {
    if (containerEl) {
      resizeObs = new ResizeObserver(() => resize());
      resizeObs.observe(containerEl);
    }
    resize();
    window.addEventListener('keydown', onKey);
    // Auto-add an Output node if the graph is empty so users have a target.
    if (app.paramGraph.nodes.length === 0) {
      addNode('primitive.box', cw / 2 - 100, ch / 2);
      addNode('output.mesh', cw / 2 + 200, ch / 2);
    }
  });
  onDestroy(() => {
    if (resizeObs) resizeObs.disconnect();
    window.removeEventListener('keydown', onKey);
  });

  let categories = $derived(nodesByCategory());
  let selected = $derived(app.paramGraph.nodes.find((n) => n.id === selectedNodeId) ?? null);
  let selectedDef = $derived(selected ? NODE_REGISTRY.get(selected.type) : null);
</script>

<div class="param-editor"
  bind:this={containerEl}
  onpointerdown={onCanvasPointerDown}
  onpointermove={onPointerMove}
  onpointerup={onPointerUp}
  onwheel={onWheel}
  oncontextmenu={(e) => e.preventDefault()}
>
  <!-- Grid background -->
  <div class="grid-bg" style="background-position: {view.ox % 24}px {view.oy % 24}px"></div>

  <!-- Wires (SVG over canvas) -->
  <svg class="wires" width={cw} height={ch}>
    <g style="transform: translate({view.ox}px, {view.oy}px) scale({view.scale}); transform-origin: 0 0">
      {#each app.paramGraph.wires as w (w.id)}
        {@const e = getWireEndpoints(w)}
        {#if e}
          <path d={wirePath(e.x1, e.y1, e.x2, e.y2)}
            stroke="#4ab8ff" stroke-width="1.6" fill="none"
            stroke-linecap="round"
            class="wire"
            onclick={() => deleteWire(w.id)} />
        {/if}
      {/each}
      {#if drag?.kind === 'wire'}
        {@const fn = app.paramGraph.nodes.find((n) => n.id === drag.fromNode)}
        {@const fdef = fn ? NODE_REGISTRY.get(fn.type) : null}
        {@const fIdx = fdef ? fdef.outputs.findIndex((p) => p.id === drag.fromPort) : -1}
        {#if fn && fdef && fIdx >= 0}
          {@const wpos = screenToWorld(drag.mouseX, drag.mouseY)}
          <path d={wirePath(portX(fn, 'out'), portY(fn, fdef.inputs.length + fIdx), wpos.x, wpos.y)}
            stroke="#f59e4b" stroke-width="1.6" fill="none"
            stroke-dasharray="4 3" />
        {/if}
      {/if}
    </g>
  </svg>

  <!-- Nodes -->
  <div class="nodes-layer" style="transform: translate({view.ox}px, {view.oy}px) scale({view.scale}); transform-origin: 0 0">
    {#each app.paramGraph.nodes as node (node.id)}
      {@const def = NODE_REGISTRY.get(node.type)}
      {#if def}
        <div class="node" class:selected={selectedNodeId === node.id} class:output={node.type === 'output.mesh'}
          style="left: {node.x}px; top: {node.y}px; width: {NODE_W}px"
          onpointerdown={(e) => { e.stopPropagation(); selectedNodeId = node.id; }}
        >
          <div class="node-header" onpointerdown={(e) => onNodeHeaderDown(e, node)}>
            <span class="cat-dot" data-cat={def.category}></span>
            <span class="title">{def.label}</span>
          </div>
          <div class="ports">
            {#each def.inputs as p, i (p.id)}
              <div class="row in-row">
                <span class="port in" data-type={p.type}
                  onpointerup={(e) => onInPortUp(e, node, p.id)}
                ></span>
                <span class="lbl">{p.name}</span>
              </div>
            {/each}
            {#each def.outputs as p, i (p.id)}
              <div class="row out-row">
                <span class="lbl right">{p.name}</span>
                <span class="port out" data-type={p.type}
                  onpointerdown={(e) => onOutPortDown(e, node, p.id)}
                ></span>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/each}
  </div>

  <!-- Floating toolbar -->
  <div class="toolbar">
    <button class="t-chip" onclick={(e) => { menuPos = { x: e.clientX - containerEl!.getBoundingClientRect().left, y: e.clientY - containerEl!.getBoundingClientRect().top }; menuOpen = !menuOpen; }}>
      <Icon name="plus" size={13} /> Node Ekle
    </button>
    <button class="t-chip" onclick={addOutputIfMissing} title="Çıktı node'u yoksa ekle">
      <Icon name="target" size={13} /> Çıktı
    </button>
    <button class="t-chip danger" onclick={clearGraph}>
      <Icon name="trash" size={13} /> Sıfırla
    </button>
    <span class="status">
      <Icon name="workflow" size={12} />
      {app.paramGraph.nodes.length} node • {app.paramGraph.wires.length} bağlantı • {triCount.toLocaleString()} ▲
    </span>
    {#if evalErrors.length > 0}
      <span class="err"><Icon name="alert-circle" size={12} /> {evalErrors.length} hata</span>
    {/if}
  </div>

  <!-- Add-node menu -->
  {#if menuOpen}
    <div class="add-menu" style="left: {menuPos.x}px; top: {menuPos.y}px"
      onclick={(e) => e.stopPropagation()}
      role="menu">
      {#each [...categories] as [cat, defs] (cat)}
        <div class="menu-cat">{cat}</div>
        {#each defs as def (def.type)}
          <button class="menu-item" onclick={() => addNode(def.type, menuPos.x, menuPos.y)}>
            {def.label}
          </button>
        {/each}
      {/each}
    </div>
  {/if}

  <!-- Inspector for selected node params -->
  {#if selected && selectedDef}
    <div class="inspector" onpointerdown={(e) => e.stopPropagation()}>
      <div class="i-title">{selectedDef.label}</div>
      {#each selectedDef.inputs as p (p.id)}
        {#if p.type === 'number' && !app.paramGraph.wires.some((w) => w.toNode === selected!.id && w.toPort === p.id)}
          <label class="i-row">
            <span>{p.name}</span>
            <input type="number" step="0.5"
              value={selected.params[p.id] ?? p.defaultValue ?? 0}
              oninput={(e) => onParamInput(selected!, p.id, (e.currentTarget as HTMLInputElement).value)} />
          </label>
        {/if}
      {/each}
      <button class="del" onclick={deleteSelected}>
        <Icon name="trash" size={11} /> Sil (Del)
      </button>
    </div>
  {/if}
</div>

<style>
  .param-editor {
    position: relative;
    width: 100%; height: 100%;
    overflow: hidden;
    background: var(--bg-viewer-from);
    border: 1px solid var(--border-soft);
    border-radius: 10px;
    user-select: none;
    cursor: default;
  }
  .grid-bg {
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
  .wires {
    position: absolute; inset: 0; pointer-events: none;
  }
  .wires .wire { pointer-events: auto; cursor: pointer; }
  .wires .wire:hover { stroke: #f59e4b; stroke-width: 2.4; }

  .nodes-layer { position: absolute; inset: 0; pointer-events: none; }
  .node {
    position: absolute;
    background: linear-gradient(180deg, #131e2e, #0c1626);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px;
    box-shadow: 0 8px 22px -8px rgba(0,0,0,0.55);
    pointer-events: auto;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .node.selected {
    border-color: var(--accent);
    box-shadow:
      0 0 0 1px rgba(74,184,255,0.32) inset,
      0 12px 28px -8px rgba(74,184,255,0.45);
  }
  .node.output { border-color: rgba(245,158,75,0.4); }
  .node.output.selected { border-color: var(--clay); box-shadow: 0 12px 28px -8px rgba(245,158,75,0.55); }
  .node-header {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 10px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    cursor: grab;
    background: rgba(255,255,255,0.03);
    border-radius: 7px 7px 0 0;
  }
  .node-header:active { cursor: grabbing; }
  .cat-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--accent);
  }
  .cat-dot[data-cat="Şekil"] { background: #34d399; }
  .cat-dot[data-cat="Dönüşüm"] { background: #fbbf24; }
  .cat-dot[data-cat="Birleştir"] { background: #c084fc; }
  .cat-dot[data-cat="Dizi"] { background: #f59e4b; }
  .cat-dot[data-cat="Çıktı"] { background: #f04a3a; }
  .title { font-size: 12px; font-weight: 600; color: var(--text-bright); }
  .ports { padding: 6px 0; }
  .row { display: flex; align-items: center; gap: 6px; padding: 1px 10px; height: 22px; font-size: 10.5px; color: var(--text-2); }
  .out-row { justify-content: flex-end; }
  .lbl.right { text-align: right; flex: 1; }
  .port {
    width: 11px; height: 11px;
    border-radius: 50%;
    background: #4ab8ff;
    border: 2px solid #0c1626;
    box-shadow: 0 0 0 1px rgba(74,184,255,0.4);
    cursor: crosshair;
  }
  .port[data-type="mesh"] { background: #34d399; box-shadow: 0 0 0 1px rgba(52,211,153,0.4); }
  .port[data-type="vec3"] { background: #fbbf24; box-shadow: 0 0 0 1px rgba(251,191,36,0.4); }
  .port[data-type="profile"] { background: #c084fc; box-shadow: 0 0 0 1px rgba(192,132,252,0.4); }
  .port.in { margin-left: -16px; }
  .port.out { margin-right: -16px; }
  .port:hover { transform: scale(1.3); }

  .toolbar {
    position: absolute; top: 12px; left: 12px;
    display: flex; gap: 6px; align-items: center;
    background: rgba(7, 17, 26, 0.85);
    backdrop-filter: blur(12px) saturate(140%);
    border: 1px solid var(--border-soft);
    padding: 6px; border-radius: 10px;
    z-index: 10;
  }
  .t-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 11px; font-size: 11px; font-weight: 600;
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid transparent; border-radius: 7px;
    cursor: pointer; font-family: inherit;
    transition: background 0.12s, color 0.12s;
  }
  .t-chip:hover { background: var(--chip-bg-hover); color: var(--text-bright); }
  .t-chip.danger { background: rgba(208,74,58,0.15); color: #ffaaa0; border-color: rgba(208,74,58,0.32); }
  .status {
    display: inline-flex; align-items: center; gap: 5px;
    color: var(--text-3); font-size: 11px; padding: 0 8px;
    border-left: 1px solid rgba(255,255,255,0.08);
    margin-left: 4px;
  }
  .err { display: inline-flex; align-items: center; gap: 4px; color: #ffaaa0; font-size: 11px; }

  .add-menu {
    position: absolute; z-index: 20;
    background: var(--bg-2);
    border: 1px solid var(--border-soft);
    border-radius: 10px;
    box-shadow: 0 16px 40px -10px rgba(0,0,0,0.6);
    padding: 4px;
    min-width: 180px;
    max-height: 70%;
    overflow-y: auto;
  }
  .menu-cat {
    font-size: 9.5px; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: 0.1em;
    padding: 8px 10px 4px; font-weight: 600;
  }
  .menu-item {
    display: block; width: 100%;
    padding: 6px 12px; font-size: 12px;
    background: transparent; border: 0; color: var(--text-2);
    text-align: left; cursor: pointer; font-family: inherit;
    border-radius: 6px;
  }
  .menu-item:hover { background: rgba(255,255,255,0.06); color: var(--text-bright); }

  .inspector {
    position: absolute; right: 12px; top: 12px;
    width: 220px;
    background: rgba(7, 17, 26, 0.92);
    backdrop-filter: blur(12px) saturate(140%);
    border: 1px solid var(--border-soft);
    border-radius: 10px;
    padding: 10px 12px;
    z-index: 10;
  }
  .i-title { font-size: 12px; font-weight: 700; color: var(--text-bright); margin-bottom: 8px; }
  .i-row {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px; padding: 5px 0; font-size: 11px; color: var(--text-2);
  }
  .i-row input {
    width: 80px; padding: 3px 6px; font-size: 11px;
    background: var(--bg-3); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 5px;
    font-family: inherit; text-align: right;
  }
  .i-row input:focus { outline: none; border-color: var(--accent); }
  .del {
    width: 100%; margin-top: 8px;
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    padding: 6px; font-size: 11px;
    background: rgba(208,74,58,0.15); color: #ffaaa0;
    border: 1px solid rgba(208,74,58,0.32); border-radius: 6px;
    cursor: pointer; font-family: inherit;
  }
  .del:hover { background: rgba(208,74,58,0.28); }
</style>
